(function (root) {
  'use strict';
  // Native section morphs can remove DOM data attributes while retaining nodes.
  // Keep listener ownership outside the DOM, and refresh each controller's data.
  const controllers = new WeakMap();
  function findVariant(variants, id) {
    if (!Array.isArray(variants)) return null;
    const matches = variants.filter((variant) => variant && String(variant.id) === String(id));
    return matches.length === 1 ? matches[0] : null;
  }
  function storageKey(locale, currency, id) { return ['md-card-v1', locale, currency, id].join(':'); }
  function loadExactImage(image, source, srcset, placeholder, labels) {
    const previous = image.dataset.mdExpectedImage;
    const previousFailed = image.dataset.mdImageFailed === 'true';
    const token = String((Number(image.dataset.mdImageToken) || 0) + 1);
    image.dataset.mdImageToken = token;
    image.dataset.mdExpectedImage = source || '';
    image.onload = null;
    image.onerror = null;
    if (!source) {
      image.hidden = true;
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.style.visibility = '';
      image.removeAttribute('aria-busy');
      return;
    }
    const absolute = (url) => new URL(url, image.ownerDocument.baseURI).href;
    const approved = [source, ...(srcset || '').split(',').map((item) => item.trim().split(/\s+/)[0]).filter(Boolean)].map(absolute);
    const isLoaded = () => image.complete && image.naturalWidth > 0 && approved.includes(absolute(image.currentSrc || image.src));
    const finish = () => {
      if (image.dataset.mdImageToken !== token || !isLoaded()) return;
      image.style.visibility = '';
      image.dataset.mdImageFailed = 'false';
      image.removeAttribute('aria-busy');
      if (placeholder) placeholder.hidden = true;
    };
    image.hidden = false;
    if (previous !== source || !isLoaded()) {
      image.style.visibility = 'hidden';
      image.setAttribute('aria-busy', 'true');
      if (placeholder) { placeholder.hidden = false; placeholder.textContent = labels.loading || ''; }
    }
    image.onload = finish;
    const fail = () => {
      if (image.dataset.mdImageToken !== token) return;
      image.hidden = true;
      image.dataset.mdImageFailed = 'true';
      image.removeAttribute('aria-busy');
      if (placeholder) { placeholder.hidden = false; placeholder.textContent = labels.error || ''; }
    };
    image.onerror = fail;
    const unchanged = image.src === absolute(source) && (image.getAttribute('srcset') || '') === (srcset || '');
    if (unchanged && ((previous === source && previousFailed) || (image.complete && image.naturalWidth === 0))) { fail(); return; }
    image.dataset.mdImageFailed = 'false';
    if (srcset) { if (image.getAttribute('srcset') !== srcset) image.setAttribute('srcset', srcset); }
    else image.removeAttribute('srcset');
    if (image.src !== absolute(source)) image.src = source;
    finish();
  }

  function selectVariant(card, variant, persist) {
    if (!variant || variant.available === false) return false;
    const image = card.querySelector('[data-md-card-image]');
    const placeholder = card.querySelector('[data-md-card-placeholder]');
    const price = card.querySelector('[data-md-card-price]');
    const note = card.querySelector('[data-md-card-note]');
    card.dataset.selectedVariant = String(variant.id);
    card.querySelectorAll('[data-md-card-link]').forEach((link) => { link.href = variant.url; if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', variant.alt || variant.title || ''); });
    card.querySelectorAll('[data-md-card-variant]').forEach((choice) => {
      const selected = String(variant.id) === choice.dataset.mdCardVariant;
      choice.setAttribute('role', 'button');
      choice.setAttribute('aria-pressed', String(selected));
      choice.dataset.mdSelected = String(selected);
    });
    if (price) price.textContent = variant.priceText;
    if (note) note.textContent = variant.note;
    if (placeholder) { placeholder.textContent = variant.note; placeholder.hidden = Boolean(variant.image); }
    if (image) {
      image.alt = variant.alt || '';
      image.width = Number(variant.width) || 1254;
      image.height = Number(variant.height) || 1254;
      loadExactImage(image, variant.image, variant.srcset, placeholder, {loading: card.dataset.imageLoading, error: card.dataset.imageError});
    }
    const approved = card.querySelector('[data-md-approved-card]');
    if (approved) approved.dataset.variantId = String(variant.id);
    if (persist) {
      try { sessionStorage.setItem(storageKey(card.dataset.locale, card.dataset.currency, card.dataset.productId), String(variant.id)); } catch (_) {}
    }
    return true;
  }
  function mount(card) {
    let controller = controllers.get(card);
    if (controller) controller.payload = null;
    let payload;
    try { payload = JSON.parse(card.querySelector('[data-md-card-data]').textContent); } catch (_) { return; }
    if (!payload || !Array.isArray(payload.variants)) return;
    if (!controller) {
      controller = { payload };
      controllers.set(card, controller);
      card.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-md-card-variant]');
        if (!choice || !card.contains(choice)) return;
        const next = findVariant(controller.payload?.variants, choice.dataset.mdCardVariant);
        if (!next || next.available === false) return;
        event.preventDefault();
        event.stopPropagation();
        selectVariant(card, next, true);
      });
      card.addEventListener('keydown', (event) => {
        const choice = event.target.closest('[data-md-card-variant]');
        if (event.key === ' ' && choice && card.contains(choice)) { event.preventDefault(); choice.click(); }
      });
    } else controller.payload = payload;
    const key = storageKey(card.dataset.locale, card.dataset.currency, card.dataset.productId);
    let remembered;
    try { remembered = sessionStorage.getItem(key); } catch (_) {}
    const initial = card.querySelector('[data-md-approved-card]')?.dataset.variantId;
    const saved = findVariant(payload.variants, remembered);
    const variant = (saved && saved.available !== false ? saved : null) || findVariant(payload.variants, initial);
    if (variant) selectVariant(card, variant, false);
    if (card.dataset.mdCardMounted !== 'true') card.dataset.mdCardMounted = 'true';
  }
  function mountAll(container) {
    if (container.matches?.('[data-md-card-root]')) mount(container);
    container.querySelectorAll('[data-md-card-root]').forEach(mount);
  }
  root.MDCardSelection = { findVariant, storageKey, selectVariant, mount, mountAll };
  if (typeof document !== 'undefined') {
    const start = () => {
      mountAll(document);
      new MutationObserver((changes) => {
        changes.forEach((change) => {
          if (change.type === 'attributes') {
            // Only server removal/reset of our marker requires reconciliation.
            // Ignore our own marker write to avoid a self-triggered render loop.
            if (change.target.matches('[data-md-card-root]') && change.target.dataset.mdCardMounted !== 'true') mount(change.target);
          } else {
            change.addedNodes.forEach((node) => { if (node.nodeType === 1) mountAll(node); });
          }
        });
      }).observe(document.getElementById('MainContent') || document.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['data-md-card-mounted'],
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
    document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
    document.addEventListener('shopify:collection:update', (event) => {
      const container = event.target.closest?.('results-list') || document;
      // A stationary card can be morphed without adding any element nodes.
      // Re-read its final payload only after the native renderer has finished.
      Promise.resolve(event.promise).then(() => {
        mountAll(container.isConnected === false ? document : container);
      }).catch(() => { /* Native filtering owns its error UI; leave fallback links intact. */ });
    });
    window.addEventListener('pageshow', () => mountAll(document));
  }
})(globalThis);
